import { useState, useEffect } from "react";
import { z } from "zod";
import { PopoverClose } from "@radix-ui/react-popover";
import { InfoIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { attributesValidator } from "../../models/schemasV2";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { HandleColumnActionParams } from "../../hooks/templateV2/types";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { useAuth } from "../../contexts/authContext.tsx";

interface ColumnPopoverProps {
  onSave: (data: HandleColumnActionParams) => void;
  tableId: string;
  column?: z.infer<typeof attributesValidator>;
  columnOrder?: number;
}

const ColumnPopover = ({
  onSave,
  tableId,
  column,
  columnOrder,
}: ColumnPopoverProps) => {
  const [columnName, setColumnName] = useState<string>(column?.name || "");
  const [description, setDescription] = useState<string>(
    column?.description || "",
  );
  const [columnType, setColumnType] = useState<string>(
    column?.type || "string",
  );
  const [unit, setUnit] = useState<string | undefined>(
    column?.unit || undefined,
  );
  const [presets, setPresets] = useState<string[]>(column?.enum || []);
  const [required, setRequired] = useState<boolean>(column?.required ?? false);
  const [inferred, setInferred] = useState<boolean>(column?.inferred ?? false);
  const auth = useAuth();
  useEffect(() => {
    if (column) {
      setColumnName(column.name);
      setDescription(column.description);
      setRequired(column.required);
      setPresets(column.enum || []);
      setInferred(column.inferred);
    }
  }, [column]);

  const isEditMode = !!column;

  const handleSave = () => {
    onSave({
      tableId,
      columnId: column?.id,
      columnName,
      description,
      required,
      inferred,
      columnType,
      columnOrder: column?.attributeOrder ?? columnOrder ?? 0,
      unit,
      presets,
      deleteColumn: false,
    });
  };

  const handleDelete = () => {
    if (isEditMode) {
      onSave({
        tableId,
        columnId: column.id,
        columnName,
        columnOrder: column?.attributeOrder ?? 0,
        description,
        deleteColumn: true,
        required,
        columnType,
        unit,
        presets,
        inferred,
      });
    }
  };

  const handleAddPreset = () => {
    if (presets) {
      setPresets([...presets, ""]);
    } else {
      setPresets([""]);
    }
  };

  const handlePresetChange = (index: number, value: string) => {
    if (presets) {
      const newPresets = [...presets];
      newPresets[index] = value;
      setPresets(newPresets);
    }
  };

  const handleDeletePreset = (index: number) => {
    if (presets) {
      const newPresets = presets.filter((_, i) => i !== index);
      setPresets(newPresets);
    }
  };

  return (
    <div className="flex flex-col gap-2 overflow-y-auto max-h-[calc(var(--radix-popover-content-available-height)-50px)] px-1">
      {isEditMode && (
        <PopoverClose asChild>
          <div className="flex justify-end">
            <TrashIcon
              className="cursor-pointer"
              onClick={handleDelete}
              size={16}
            />
          </div>
        </PopoverClose>
      )}
      <div className="flex flex-col gap-1">
        <label htmlFor="columnName" className="text-sm">
          Column Name
        </label>
        <Input
          placeholder="Column Name"
          type="text"
          id="columnName"
          onChange={(e) => setColumnName(e.target.value)}
          value={columnName}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="aiInstructions" className="text-sm">
          AI Instructions (Optional)
        </label>
        <Textarea
          placeholder="AI Instructions"
          id="aiInstructions"
          onChange={(e) => setDescription(e.target.value)}
          value={description}
        />
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem className="border-0" value="item-1">
          <AccordionTrigger className="text-sm font-normal">
            Advanced Settings
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="required"
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked)}
                />
                <label htmlFor="required">Required</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="inferred"
                  checked={inferred}
                  onCheckedChange={(checked) => setInferred(checked)}
                />
                <label htmlFor="inferred" className="flex items-center gap-1">
                  Inferred from AI (Beta)
                  <HoverCard openDelay={100}>
                    <HoverCardTrigger>
                      <InfoIcon
                        size={14}
                        className="text-muted-foreground cursor-pointer"
                      />
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80 p-4">
                      <p className="text-sm">
                        When enabled, this attribute's value will be
                        automatically inferred by AI instead of pulled from the
                        uploaded document. For example: If you want a summary of
                        this row, you can enable this and name the column
                        "summary". This is a beta feature.
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </label>
              </div>
            </div>
            <div className="flex gap-2 p-1 mb-8">
              <div className="flex flex-col gap-1 w-1/2">
                <label className="text-sm">Data Type</label>
                <Select
                  value={columnType}
                  onValueChange={(value) => {
                    if (value !== "number") {
                      setUnit(undefined);
                    }
                    if (value !== "string") {
                      setPresets([]);
                    }
                    setColumnType(value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a data type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">True/False</SelectItem>
                    {auth.workspace?.imageExtractionsEnabled && (
                      <SelectItem value="image">Image Extract</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {/*{columnType === "number" && (*/}
              {/*  <div className="flex flex-col gap-1 w-1/2">*/}
              {/*    <label className="text-sm">Base Unit</label>*/}
              {/*    <Select*/}
              {/*      value={unit}*/}
              {/*      onValueChange={(value) =>*/}
              {/*        setUnit(value === "none" ? undefined : value)*/}
              {/*      }*/}
              {/*    >*/}
              {/*      <SelectTrigger>*/}
              {/*        <SelectValue placeholder="Select a base unit" />*/}
              {/*      </SelectTrigger>*/}
              {/*      <SelectContent>*/}
              {/*        <SelectItem value="none">None</SelectItem>*/}
              {/*        <SelectItem value="day">Daily</SelectItem>*/}
              {/*        <SelectItem value="week">Weekly</SelectItem>*/}
              {/*        <SelectItem value="month">Monthly</SelectItem>*/}
              {/*        <SelectItem value="year">year</SelectItem>*/}
              {/*      </SelectContent>*/}
              {/*    </Select>*/}
              {/*  </div>*/}
              {/*)}*/}
              {columnType === "string" && (
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="text-sm">Presets</label>
                  <div className="flex flex-col gap-2">
                    {presets.map((preset, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={preset}
                          onChange={(e) =>
                            handlePresetChange(index, e.target.value)
                          }
                        />
                        <TrashIcon
                          size={16}
                          className="cursor-pointer"
                          onClick={() => handleDeletePreset(index)}
                        />
                      </div>
                    ))}
                    <Button
                      onClick={handleAddPreset}
                      className="flex items-center gap-1"
                    >
                      <PlusIcon size={16} />
                      Add Preset
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="sticky bottom-0 bg-white p-2">
        <PopoverClose asChild>
          <Button className="w-full" onClick={handleSave}>
            Save
          </Button>
        </PopoverClose>
      </div>
    </div>
  );
};

export default ColumnPopover;
